import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { isRefusal, refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { ChapterSectionView, ChapterView } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button, Card, EmptyState, Loading, Pill, SafeMarkdown } from "../../../ui";
import { FormError, TextArea, TextInput } from "../components/fields";
import { ElementMarks } from "../components/marks";
import { useCommandId, useRefresh } from "../hooks";
import { isSettled } from "../lib/badges";
import { chapterPath } from "../lib/paths";
import { keyFromTitle } from "../lib/text";

const SECTION_STATUS: Record<string, string> = {
  draft: "Draft: it stays in the app until it is settled",
  settled: "Settled",
  declined: "Declined",
};

/** The screen at /chapters/:key: write and settle the sections of a specification chapter. */
export function ChapterPage() {
  const { key = "" } = useParams();
  const me = useCurrentMe();
  const editable = me.active?.roles.includes("engineer") ?? false;
  const list = useApiQuery("list_chapters", {}, { staleTime: 15_000 });
  const chapter = useApiQuery("chapter", { key }, { enabled: !!key, retry: false });
  const missing = chapter.isError && isRefusal(chapter.error, "not_found");
  const view: ChapterView | null = chapter.data ?? (missing ? { chapter_key: key, title: key.replace(/_/g, " "), element: null, sections: [] } : null);

  return (
    <div className="stack item-screen">
      <div className="chapter-layout">
        <aside className="chapter-layout__list stack" aria-label="Chapters">
          <Card aria-labelledby="chapters-title">
            <h2 id="chapters-title">Chapters</h2>
            {list.isPending ? (
              <Loading>Reading the chapters</Loading>
            ) : (list.data ?? []).length === 0 ? (
              <EmptyState title="No chapter yet">A chapter is a piece of the specification that people write.</EmptyState>
            ) : (
              <ul className="chapter-list">
                {(list.data ?? []).map((entry) => (
                  <li key={entry.chapter_key} data-current={entry.chapter_key === key || undefined}>
                    <Link to={chapterPath(entry.chapter_key)} aria-current={entry.chapter_key === key ? "page" : undefined}>
                      {entry.title || entry.chapter_key}
                    </Link>
                    {entry.element && <ElementMarks element={entry.element} />}
                  </li>
                ))}
              </ul>
            )}
            {editable && <NewChapter />}
          </Card>
        </aside>

        <div className="chapter-layout__main stack">
          {chapter.isPending && !missing ? (
            <Loading>Reading the chapter</Loading>
          ) : chapter.isError && !missing ? (
            <Card>
              <EmptyState title="This chapter could not be opened">{refusalText(chapter.error)}</EmptyState>
            </Card>
          ) : view ? (
            <ChapterBody view={view} isNew={missing} editable={editable} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function NewChapter() {
  const [title, setTitle] = useState("");
  const navigate = useNavigate();
  const key = keyFromTitle(title);
  return (
    <form
      className="new-chapter"
      onSubmit={(event) => {
        event.preventDefault();
        if (key) void navigate(chapterPath(key));
      }}
    >
      <TextInput label="Start a new chapter" hint="Give it a title. Its first section comes next." value={title} onChange={setTitle} />
      <Button type="submit" disabled={!key}>
        Start the chapter
      </Button>
    </form>
  );
}

function ChapterBody({ view, isNew, editable }: { view: ChapterView; isNew: boolean; editable: boolean }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(isNew);
  const element = view.element ?? null;
  const settled = element ? isSettled(element) : false;

  return (
    <>
      <Card aria-labelledby="chapter-title">
        <h1 id="chapter-title">{view.title || view.chapter_key}</h1>
        <p className="element-meta">
          <code>{view.chapter_key}</code> {element ? <ElementMarks element={element} /> : <Pill>Not written to the files yet</Pill>}
        </p>
        {settled && <p className="muted">This chapter is validated. An edit of a section becomes a Next version for the SME, written beside the validated text.</p>}
        {isNew && <p className="muted">There is no chapter with this key yet. Write its first section to start it.</p>}
      </Card>

      {view.sections.length === 0 && !adding ? (
        <Card>
          <EmptyState
            title="No sections yet"
            action={
              editable ? (
                <Button variant="primary" onClick={() => setAdding(true)}>
                  Write the first section
                </Button>
              ) : undefined
            }
          >
            A chapter is written in sections. A section stays a draft in the app until it is settled.
          </EmptyState>
        </Card>
      ) : (
        view.sections.map((section) => (
          <SectionCard
            key={section.key}
            chapterKey={view.chapter_key}
            section={section}
            editable={editable}
            editing={editing === section.key}
            onEdit={() => setEditing(section.key)}
            onDone={() => setEditing(null)}
          />
        ))
      )}

      {editable && !adding && view.sections.length > 0 && (
        <div className="row">
          <Button onClick={() => setAdding(true)}>Add a section</Button>
          {!element && <SendChapter view={view} />}
        </div>
      )}
      {editable && adding && <NewSection chapterKey={view.chapter_key} existing={view.sections.map((s) => s.key)} onDone={() => setAdding(false)} />}
    </>
  );
}

/**
 * "Send this chapter to the SME" (CP-08): a chapter written by hand becomes an element, added by hand, from its section
 * drafts, and the SME validates it like any other. It has a route of its own (`send_chapter`, completion round). The server
 * refuses while a section still has an open card, and when the chapter was sent already.
 */
function SendChapter({ view }: { view: ChapterView }) {
  const add = useApiCommand("send_chapter");
  const ids = useCommandId();
  const refresh = useRefresh();
  const title = (view.title || view.chapter_key).trim();
  return (
    <>
      <Button
        variant="primary"
        busy={add.isPending}
        onClick={() =>
          add.mutate(
            { key: view.chapter_key, title, command_id: ids.current() },
            {
              onSuccess: () => {
                ids.renew();
                refresh("chapter", "list_chapters", "inbox", "item_page");
              },
            },
          )
        }
      >
        Send this chapter to the SME
      </Button>
      <FormError message={add.error ? refusalText(add.error) : null} />
    </>
  );
}

function SectionCard({
  chapterKey,
  section,
  editable,
  editing,
  onEdit,
  onDone,
}: {
  chapterKey: string;
  section: ChapterSectionView;
  editable: boolean;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
}) {
  return (
    <Card aria-label={section.title || section.key}>
      {editing ? (
        <SectionEditor chapterKey={chapterKey} sectionKey={section.key} initialTitle={section.title} initialText={section.text} onDone={onDone} />
      ) : (
        <>
          <div className="section__head row">
            <h2>{section.title || section.key}</h2>
            <Pill>{SECTION_STATUS[section.status] ?? section.status}</Pill>
            {section.card_id && <span className="muted">Card {section.card_id}</span>}
          </div>
          {section.text.trim() ? <SafeMarkdown>{section.text}</SafeMarkdown> : <p className="muted">This section has no text yet.</p>}
          {editable && (
            <Button variant="secondary" onClick={onEdit}>
              Edit this section
            </Button>
          )}
        </>
      )}
    </Card>
  );
}

function SectionEditor({
  chapterKey,
  sectionKey,
  initialTitle,
  initialText,
  onDone,
}: {
  chapterKey: string;
  sectionKey: string;
  initialTitle: string;
  initialText: string;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [text, setText] = useState(initialText);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useApiCommand("edit_chapter_section");
  const ids = useCommandId();
  const refresh = useRefresh();

  const submit = () => {
    const found: Record<string, string> = {};
    if (!title.trim()) found.title = "Give the section a title.";
    if (!text.trim()) found.text = "Write the text of the section.";
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    save.mutate(
      { key: chapterKey, section: sectionKey, title: title.trim(), text, command_id: ids.current() },
      {
        onSuccess: () => {
          ids.renew();
          refresh("chapter", "list_chapters", "session_cards", "item_page");
          onDone();
        },
      },
    );
  };

  return (
    <form
      className="section-editor"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <TextInput label="Title of the section" required value={title} error={errors.title} onChange={setTitle} />
      <div className="section-editor__pair">
        <TextArea label="Text" required rows={10} hint="You can use simple formatting: headings, lists and emphasis." value={text} error={errors.text} onChange={setText} />
        <div className="section-editor__preview" aria-label="How it will read">
          <p className="field__label">How it will read</p>
          {text.trim() ? <SafeMarkdown>{text}</SafeMarkdown> : <p className="muted">Nothing to show yet.</p>}
        </div>
      </div>
      <div className="row">
        <Button type="submit" variant="primary" busy={save.isPending}>
          Save the section
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
      <FormError message={save.error ? refusalText(save.error) : null} />
    </form>
  );
}

function NewSection({ chapterKey, existing, onDone }: { chapterKey: string; existing: string[]; onDone: () => void }) {
  const [title, setTitle] = useState("");
  const [started, setStarted] = useState(false);
  const base = keyFromTitle(title);
  const key = base && existing.includes(base) ? `${base}_${existing.length + 1}` : base;
  if (!started) {
    return (
      <Card aria-label="A new section">
        <h2>A new section</h2>
        <TextInput label="Title of the section" value={title} onChange={setTitle} />
        <div className="row">
          <Button variant="primary" disabled={!key} onClick={() => setStarted(true)}>
            Write it
          </Button>
          <Button onClick={onDone}>Cancel</Button>
        </div>
      </Card>
    );
  }
  return (
    <Card aria-label="A new section">
      <SectionEditor chapterKey={chapterKey} sectionKey={key} initialTitle={title.trim()} initialText="" onDone={onDone} />
    </Card>
  );
}
